<template>
  <div id="jumbotron">
    <div class="overlay"></div> <!-- Blue blur overlay -->
    <div class="container content">
      <p class="title">
        Welcome to English Matters
        <br>and More!
      </p>
    </div>
  </div>

  <div id="content" class="container mt-5">
    <div class="yt-container mb-5">
      <iframe width="560" height="315" 
              src="https://www.youtube.com/embed/kZbFIVmc7bo?si=DV7cGPcZsEyBfdal" 
              title="YouTube video player" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
    </div>

    <h1>
      🧠 Most English Classes Focus on the Language.
      <br>We Focus on the Child.
    </h1>

    <div class="text-center py-4">
      <img src="https://english-matters.s3.ap-southeast-1.amazonaws.com/photos/Classes+Creative+Writing.webp"
          alt="Creative Writing Class" class="w-100" style="max-width: 600px">
    </div>

    <h2 class="text-success">
      😟 Does This Sound Familiar?
    </h2>
    <ol>
        <li class="mb-4">
          <strong>Have you ever wondered why your child still struggles to express themselves, even after years of tuition?</strong>
          <br>Is it really about grammar… or something deeper that no one’s addressing?
        </li>
        <li class="mb-4">
          <strong>Does your child keep second-guessing themselves — afraid to speak up, afraid to be wrong?</strong>
          <br>What if that fear is the real thing holding them back, not their English?
        </li>
        <li class="mb-4">
          <strong>Are you worried your child is falling behind — not just in school, but in confidence and self-belief?</strong>
          <br>And no matter how much you spend on classes… it doesn’t seem to change.
        </li>
        <li class="mb-4">
          <strong>Is your child bright, but easily discouraged or disengaged in the classroom?</strong>
          <br>Do you sometimes feel like no one’s helping them discover their true strengths?
        </li>
        <li class="mb-4">
          <strong>What if your child’s potential is being limited — not by ability, but by a lack of personal growth support in their learning?</strong>
          <br>Because real learning isn’t just academic. It’s emotional and mental too.
        </li>
    </ol>

    <div class="text-center">
      <img src="https://english-matters.s3.ap-southeast-1.amazonaws.com/campaigns/headache.png" class="w-100" style="max-width: 700px;"/>
    </div>

    
  </div>
  <div class="bg py-4 my-5">
    <div class="container">
      <h2>✅ Here's What Makes English Matters Different</h2>
      <p>
        Let me tell you a story.
      </p>
      <p>
        A mother once came to us, worried about her 8-year-old son.
        <br>He understood English — but refused to speak it.
        <br>He’d hide behind others in group activities. He’d write only when forced.
        <br>And when he got something wrong? He’d shut down completely.
      </p>
      <p>
        She had tried tuition centres. She’d tried school support. Nothing worked.
      </p>
      <p>
        What she didn’t realise — and what most don’t — is this:
      </p>
      <p class="fw-bold">
        The problem wasn’t his English. It was how he saw himself.
      </p>
    </div>
  </div>

    
  <div class="container">
    <div>
      <h2>🌱 That’s why at English Matters, we do things differently.</h2>
      <p>
        We don’t just teach English.
        <br>We develop the <strong>child behind the language</strong>.
      </p>
    </div>

    <hr class="my-5">
    
    <div>
      <h2>💡 What We Focus On — And Why It Matters:</h2>
      <div class="mb-4">
        <p>
          <strong class="fw-bold">🧠 Self-Awareness, Not Just Vocabulary</strong>
          <br>Children discover their strengths, learning styles, and how to grow from within.
          <br>✅ <span class="fst-italic">Result</span>: They stop comparing and start communicating.</p>
      </div>
      
      <div class="mb-4">
        <p>
          <strong class="fw-bold">🗣️ Confidence, Not Just Grammar</strong>
          <br>Public speaking and group activities help them find their voice.
          <br>✅ <span class="fst-italic">Result</span>: They speak up in class, lead conversations, and believe in themselves.</p>
      </div>
      
      <div class="mb-4">
        <p>
          <strong class="fw-bold">💪 Resilience, Not Just Writing Structure</strong>
          <br>We teach kids how to try, fail, and bounce back — stronger.
          <br>✅ <span class="fst-italic">Result</span>: They become problem-solvers, not perfectionists.</p>
      </div>
      
      <div class="text-center mb-4">
        <img src="https://english-matters.s3.ap-southeast-1.amazonaws.com/campaigns/pdp.jpg" class="w-100" style="max-width: 700px;"/>
      </div>

      <div class="mb-4">
        <p>
          <strong class="fw-bold">🔍 Critical Thinking, Not Just Comprehension</strong>
          <br>They don’t just memorise; they understand and connect.
          <br>✅ <span class="fst-italic">Result</span>: They think deeper, ask smarter questions, and retain more.</p>
      </div>
      
      <div class="mb-4">
        <p>
          <strong class="fw-bold">❤️ Personal Growth, Not Just Academic Growth</strong>
          <br>Through our Personal Development Program (PDP) and WHALE Room™ experience, we raise confident, empathetic, and expressive individuals.
          <br>✅ <span class="fst-italic">Result</span>: Your child thrives — in school, in friendships, and in life.</p>
      </div>
    </div>
  </div>

  <div class="bg py-4 my-5">
    <div class="container">
      <h2>🧩 So… What’s the First Step?</h2>
      <p>Now you might be wondering…</p>
      <p class="fst-italic">“This all sounds great — but how do I know if it’s right for <strong>my</strong> child?”</p>
      <p>
        We get it.
        <br>Every child is different. That’s why we don’t believe in cookie-cutter programs.
      </p>
      <p>To truly help your child thrive — <strong>we need to understand them first</strong>.</p>
      <p>That’s exactly why we’ve put together a <strong>FREE parent talk</strong>, where you’ll not only hear how our proven system works…</p>
      <p>…but also get a <strong>crystal-clear picture</strong> of your child’s English level, personality strengths, and growth potential.</p>   
    </div>
  </div>

  <div class="container">
    <h2>🎁 Just for Attending, You’ll Receive (Worth RM450 – Yours FREE):</h2>
    <ol>
      <li class="mb-4">
        <strong>✅ English Proficiency Assessment (RM100 Value)</strong>
        <br>Know your child’s true English strengths and weaknesses — no more guesswork.
      </li>
      <li class="mb-4">
        <strong>✅ Personality & Learning Style Analysis (RM250 Value)</strong>
        <br>Discover how your child learns best, and what motivates or blocks them.
      </li>
      <li class="mb-4">
        <strong>✅ Trial Class in Our WHALE Room (RM100 Value)</strong>
        <br>Let your child experience the difference — not just sit through another class.
      </li>
    </ol>

    <div class="text-center pt-4">
      <img src="https://english-matters.s3.ap-southeast-1.amazonaws.com/campaigns/profile.jpg" alt="Career Profile" style="max-width: 700px" class="w-100">
    </div>
  </div>

  <div class="bg py-4 my-5">
    <div class="container">
      <h2>💥 Total Value: RM450 — Yours FREE</h2>
      <p>No strings attached. No hidden commitments. Sign up now.</p>
    </div>
  </div>
  <div class="container">
    <div>
      <h2>💡 At the Talk, You’ll Learn:</h2>
      <ul>
        <li>The #1 invisible reason why most children struggle with English (and it’s NOT what you think)</li>
        <li>How to turn shy, disengaged kids into confident, articulate learners</li>
        <li>Why more tuition is not the answer — and what actually works</li>
        <li>How to activate your child’s natural learning and leadership strengths</li>
        <li>What you can do at home to support real growth</li>
      </ul>
  
      <div class="text-center">
        <a href="https://forms.gle/YweQPtMrejUGh4B1A" target="_blank" alt="Sign Up Now" class="btn btn-primary">
          Sign Up
        </a>
      </div>
    </div>

  </div>

  <div class="bg py-4 my-5">
    <div class="container">
      <h2>🙋‍♀️ Still Unsure If You Should Attend?</h2>
      <p>Here’s what you might be thinking:</p>
    </div>
  </div>

  <div class="container">
    <p>
      <strong>❌ “My child is already attending tuition — isn’t that enough?”</strong>
      <br>💬 Not if they still lack confidence or motivation.
      <br>We fix the <span class="fst-italic">real root cause</span> — by working on the child, not just the syllabus.
    </p>

    <p>
      <strong>❌ “My child is too shy or weak in English”</strong>
      <br>💬 Perfect. That means they’ll benefit the most.
      <br>Our program was made <span class="fst-italic">for</span> kids like yours.
    </p>

    <p>
      <strong>❌ “I’m not ready to commit”</strong>
      <br>💬 Good — because you don’t have to.
      <br>This is just a talk, an assessment, and a trial. All free.
    </p>

    <p>
      <strong>❌ “We’re too busy”</strong>
      <br>💬 1 hour now can unlock your child’s potential for years to come.

    </p>
    <p>
      <strong>❌ “What if my child is already doing okay?”</strong>
      <br>💬 <strong>Our take</strong>:
      <br>Then imagine how much more they could achieve if they understood themselves better, spoke with stronger confidence, and learned how to lead — not just follow.
    </p>
  </div>

  <div class="bg py-4 my-5">
    <div class="container">
      <p>
        <strong>🎯 Bottom line?</strong>
        <br>If you want your child to do more than just “pass”…
        <br>If you want them to grow into a confident communicator, a thoughtful learner, and a future leader…
        <br>Then this talk is <strong>the best 60 minutes you’ll spend this year.</strong>
      </p>
    </div>
  </div>

  <div class="container">
    <div>
      <h2 class="text-primary">💬 What Other Parents Are Saying</h2>
  
      <p class="mb-4">
        <strong>🧡  "Before this, my daughter was always the quiet one. Now, she’s leading group presentations and even reading aloud at family dinners! English Matters changed everything."</strong>
        <br><span class="fst-italic">— Mrs. Lim, mother of a 9-year-old</span>
      </p>
  
      <p class="mb-4">
        <strong>🧡  "We’ve tried tuition centres for years. Only here did we see real progress — not just in English, but in her attitude and confidence. She actually looks forward to class now!"</strong>
        <br><span class="fst-italic">— Mr. Chan, father of a 10-year-old</span>
      </p>
  
      <p class="mb-4">
        <strong>🧡  "My son used to give up at the slightest mistake. After a few months here, he’s resilient, motivated, and even helping his younger siblings with homework!"</strong>
        <br><span class="fst-italic">— Pn. Aishah, parent of an 11-year-old</span>
      </p>
    </div>
    
    <hr class="my-5">

    <h2>📅 Join Us at Our Upcoming Parent Talk</h2>
    <p>
      Limited seats. Small group format. Real value.
    </p>
    <p>
      Date and Time:
    </p>
    <ul>
      <li>29 July, 5:30pm</li>
      <li>5 August, 5:30pm</li>
      <li>12 August, 5:30pm</li>
      <li>21 August, 5:30pm</li>
      <li>26 August, 5:30pm</li>
    </ul>
    
    <div class="row my-4">
      <div class="col-12 col-md-6 mb-4">
        <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.9195770315687!2d101.60958297467602!3d3.1159776968595616!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc4eabb1d84e95%3A0xe178294f469b9a52!2sEnglish%20Matters!5e0!3m2!1sen!2smy!4v1741792093388!5m2!1sen!2smy" width="100%  " height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
      </div>
      <div class="col-12 col-md-6 mb-4">
        <strong>📍 English Matters</strong>
        <br>No.1-3, 2nd Floor,
        <br>Jalan SS 23/15, Taman SEA,
        <br>47400 Petaling Jaya, Selangor,
        <br>Malaysia
      </div>
    </div>

    <hr class="my-5">

    <p class="fw-bold">
      🎁 RM450 worth of assessments & trial — absolutely FREE
      <br>Sign up here to reserve a spot. 
    </p>

    <div class="text-center my-5">
      <a href="https://forms.gle/YweQPtMrejUGh4B1A" target="_blank" alt="Sign Up Now" class="btn btn-primary">
        Sign Up
      </a>
    </div>

    <p class="fw-bold">
      ✨ Your child’s breakthrough starts here.
      <br>Let’s build it — together.
    </p>

    <br><br><br><br>
  </div>
</template>

<style scoped>
  #content p, #content li, #content div, #content a {
    font-size: 1.1em;
  }

  .container {
    max-width: 1000px;
  }

  #jumbotron {
    position: relative;
    background-image: url("https://english-matters.s3.ap-southeast-1.amazonaws.com/photos/banner-buiding.webp");
    background-size: cover;
    background-position: center;
    min-height: 60vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Blue blurry overlay */
  #jumbotron .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(19, 5, 115, 0.75); /* Blue tint */
  }

  #jumbotron .content {
    position: relative;
    z-index: 2;
  }

  #jumbotron .title {
    color: white;
    font-size: 4em;
    font-weight: bold;
  }

  @media (max-width: 576px) {
    #jumbotron .title {
      padding-top: 60px;
      font-size: 2.5em;
    }
  }

  .yt-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio (9 / 16 = 0.5625) */
    height: 0;
    overflow: hidden;
  }

  .yt-container iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }

  .bg {
    background-color: #8ad3da;
    font-size: 1.1em;
  }
</style>